// Чистые функции вкладок «Таблица» и «История» прайса: подписи, дифф цен, фильтр.
// Логика синхронизации — doscar_back/docs/price-sheet-sync.md

export const money = (v) => (v == null ? '' : Number(v).toLocaleString('ru-RU'));

// Изменение цены в процентах; null — сравнивать не с чем (новая позиция)
export const deltaPercent = (oldPrice, newPrice) => {
  if (oldPrice == null || newPrice == null || !oldPrice) return null;
  return ((newPrice - oldPrice) / oldPrice) * 100;
};

// «+300 000 (+3,0%)» — явный дифф вместо двух чисел, которые надо вычитать в уме
export const deltaText = (oldPrice, newPrice) => {
  if (oldPrice == null || newPrice == null) return 'новая';
  const diff = newPrice - oldPrice;
  if (diff === 0) return 'без изменений';
  const sign = diff > 0 ? '+' : '−';
  const pct = Math.abs(deltaPercent(oldPrice, newPrice)).toFixed(1).replace('.', ',');
  return `${sign}${money(Math.abs(diff))} (${sign}${pct}%)`;
};

// Скачок больше этого значения бэк останавливает до подтверждения
export const BIG_JUMP = 10;

export const isBigJump = (oldPrice, newPrice) => {
  const pct = deltaPercent(oldPrice, newPrice);
  return pct != null && Math.abs(pct) > BIG_JUMP;
};

export const KIND_LABELS = {
  created: 'новая',
  changed: 'изменена',
  held: 'ждёт подтверждения',
  problem: 'проблема',
};

export const STATUS_LABELS = {
  nochange: 'без изменений',
  applied: 'применено',
  partial: 'применено частично',
  attention: 'нужно внимание',
  blocked: 'остановлено',
  error: 'ошибка',
};

// Причина проблемы человеческим языком: менеджеру не нужно знать, что такое «неоднозначна»
export const plainReason = (reason) => {
  const r = reason || '';
  let m;
  if ((m = r.match(/^марка «(.+)» не найдена/))) return `Марки «${m[1]}» нет в нашем каталоге`;
  if ((m = r.match(/^марка «(.+)» неоднозначна/))) return `Марке «${m[1]}» подходит несколько вариантов в каталоге`;
  if ((m = r.match(/^модель «(.+)» не найдена у (.+)$/))) return `У марки ${m[2]} нет модели «${m[1]}»`;
  if ((m = r.match(/^модель «(.+)» неоднозначна/))) return `Модели «${m[1]}» подходит несколько вариантов в каталоге`;
  if ((m = r.match(/^комплектация «(.+)» не найдена у (.+)$/))) return `У модели ${m[2]} нет комплектации «${m[1]}»`;
  if ((m = r.match(/^комплектация «(.+)» неоднозначна/))) return `Комплектации «${m[1]}» подходит несколько вариантов в каталоге`;
  if (/изменилось с момента привязки/.test(r)) return 'Текст строки изменился после привязки: подтвердите привязку заново';
  if (/несколько строк на одну позицию/.test(r)) return 'В таблице несколько строк на одну позицию с разной ценой';
  if (/вне разумных пределов/.test(r)) return 'Цена выглядит ошибочной (слишком мала или велика)';
  if (/не разобрана/.test(r)) return 'Не удалось прочитать цену в этой строке';
  return r;
};

// Тип проблемы по подсказкам: есть позиция с такой же ценой / позиции есть, но цена другая / подсказок нет
export const problemKind = (p) => {
  const s = p.suggestions || [];
  if (s.some((x) => x.priceMatches)) return 'exact';
  return s.length > 0 ? 'differs' : 'none';
};

// Единственная подсказка с совпавшей ценой — её можно привязать без раздумий (и массово)
export const singleExact = (p) => {
  const exact = (p.suggestions || []).filter((x) => x.priceMatches);
  return exact.length === 1 ? exact[0] : null;
};

// Статус привязанной строки (вкладка «Привязки») и что он значит для менеджера
export const LINK_STATUS = {
  ok: { label: 'сходится', hint: 'Цена в таблице равна цене правила: применять нечего.' },
  price_differs: {
    label: 'цена отличается',
    hint: 'Цена в таблице другая, чем у правила. При «Применить изменения» правило получит цену из таблицы.',
  },
  no_rule: {
    label: 'правила ещё нет',
    hint: 'Привязанного правила в приказах пока нет. При «Применить изменения» оно будет создано.',
  },
  row_changed: {
    label: 'строка изменилась',
    hint: 'Текст строки в таблице изменился после привязки, поэтому привязка не действует. Проверьте строку и сохраните привязку заново.',
  },
  row_missing: {
    label: 'нет в таблице',
    hint: 'Строки с таким ID в таблице больше нет (или таблица недоступна). Привязку можно снять.',
  },
  ignored: { label: 'не синхронизируется', hint: 'Строка исключена из синхронизации. Можно вернуть кнопкой «Вернуть в синхронизацию».' },
};

export const LINK_SOURCE = { auto: 'авто', manual: 'вручную', ignored: 'исключена' };

// Привязки, на которые стоит посмотреть: ничего не сломано, но что-то не сходится или не действует
export const linkNeedsAttention = (status) => ['row_changed', 'row_missing', 'no_rule', 'price_differs'].includes(status);

// Откатить можно применённый запуск, который ещё не откатывали
export const canRollback = (run) => ['applied', 'partial'].includes(run.status) && !run.revertedAt;

// Сколько применённых и не откаченных запусков новее выбранного: столько отменит «Вернуть к версии»
export const laterRollbackable = (runs, runId) =>
  (runs || []).filter((x) => x.run.id > runId && canRollback(x.run)).length;

export const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString('ru-RU') : '');

// «02.11 13:51» — для плотных таблиц, где год и секунды только мешают
export const formatShort = (iso) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';

// Короткая сводка запуска для строки списка
export const summarizeRun = (run) =>
  [
    run.created ? `новых ${run.created}` : null,
    run.changed ? `изменено ${run.changed}` : null,
    run.held ? `ждут ${run.held}` : null,
    run.problems ? `проблем ${run.problems}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

// Фильтр истории: по подписи позиции, марке, ID строки и примечанию
export const matchesFilter = (change, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return [change.label, change.mark, change.sheetKey, change.note].some((v) => (v || '').toLowerCase().includes(q));
};

// Только то, что реально меняет цену: без строк-проблем
export const priceChanges = (changes) => changes.filter((c) => c.kind !== 'problem');
